import React, { useState, useContext, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  createContactDetails,
  getContactDetails,
  getProfile,
  updateContactDetails,
} from "api/User";
import { UserContext } from "context/user";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { checkoutCartItems } from "api/Cart";
import { SUCCESS_STATUS } from "constants/success-status";
import Navbar from "pages/Payment/components/Navbar";
import Header from "pages/Payment/components/Header";
import Order from "pages/Payment/components/Order";
import ContactDetails from "pages/Payment/components/ContactDetails";
import PaymentMethod from "pages/Payment/components/PaymentMethod";
import BillingDetails from "pages/Payment/components/BillingDetails";
import Terms from "pages/Payment/components/Terms";
import PayButton from "pages/Payment/components/PayButton";
import CountryModal from "pages/Payment/components/CountryModal";
import "./index.scss";
import { useCartItemsQuery } from "hooks/useCart";
import { useBasePricing } from "hooks/useBasePricing";
import { useStackBasePriceQuery } from "hooks/useStackBasePriceQuery";
import { getItemPrice, getStackItemPrice } from "utils";
import { getFarm } from "api/Farm";

const PaymentContainer = () => {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { token, user } = useContext(UserContext);
  const [userProfile, setUserProfile] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [payments, setPayments] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [country, setCountry] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [countryError, setCountryError] = useState("");
  const [price, setPrice] = useState("");
  const [cartItems, setCartItems] = useState(null);

  const cartIds = cartItems?.map((a) => a.id);
  const cartQuery = cartIds?.map((id) => `cart_ids=${id}`).join("&");
  const isInvoice = paymentMethod === "invoice";
  const paymentQuery = `&invoice=${isInvoice}`;
  const query = `${cartQuery}${paymentQuery}`;

  const { data: itemsInCart } = useCartItemsQuery(token, cartItems);
  const { data: basePrice } = useBasePricing(token);
  const { data: stackPricing } = useStackBasePriceQuery();

  const getTotalPrice = useCallback(async () => {
    let subTotal = 0;
    await cartItems.map(async (cart) => {
      subTotal += cart?.price;
    });
    setPrice(subTotal.toFixed(2));
    return subTotal.toFixed(2);
  }, [cartItems, setPrice]);

  const getCartDetails = useCallback(
    async (items) => {
      const farmData = [];
      await Promise.all(
        items.map(async (item) => {
          try {
            const farmDetails = await getFarm(item.details.farm_id, token);
            farmData.push(farmDetails);
          } catch (e) {
            toast.error("Fetching Farm details error.");
          }
        })
      );
      const zoningItems = [];
      items.forEach((item) => {
        if (item.item_type === "zoning") {
          farmData.forEach((i) => {
            if (i.properties.farm_id === item.details.farm_id) {
              zoningItems.push({
                item_type: item.item_type,
                details: item.details,
                id: item.id,
                farm: i,
                price: getItemPrice(
                  item?.details?.zones?.features,
                  basePrice?.hectare_cost
                ),
              });
            }
          });
        }
        if (item.item_type === "stack analysis order") {
          farmData.forEach((i) => {
            if (i.properties.farm_id === item.details.farm_id) {
              zoningItems.push({
                item_type: item.item_type,
                details: item.details,
                id: item.id,
                farm: i,
                price: getStackItemPrice(
                  item?.details,
                  stackPricing?.unit_cost
                ),
              });
            }
          });
        }
      });
      const result = [
        ...zoningItems
          .reduce((a, c) => {
            a.set(c.id, c);
            return a;
          }, new Map())
          .values(),
      ];
      setCartItems(result);
    },
    [token, basePrice?.hectare_cost, stackPricing?.unit_cost]
  );

  useEffect(() => {
    if (!itemsInCart) return;
    if (itemsInCart) {
      getCartDetails(itemsInCart);
    }
  }, [itemsInCart, getCartDetails]);

  useEffect(() => {
    if (cartItems) {
      getTotalPrice();
    }
  }, [getTotalPrice, cartItems]);

  const [cardDetails, setCardDetails] = useState({
    name: "",
  });

  const [cardErrorMessage, setCardErrorMessage] = useState({
    name: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvc: "",
  });

  const handleSelectCountry = (val) => {
    if (val === "" || val === null) {
      setCountryError("Select country");
    } else {
      setCountryError("");
    }
    setCountry(val.toUpperCase());
  };

  const isErrorListHasValue = (errorList) => {
    const isListHasError = Object.values(errorList).some(
      (error) => error !== null && error !== "" && typeof error !== "undefined"
    );
    return isListHasError;
  };

  const handleSelectPaymentMethod = (event, method) => {
    setPaymentMethod(method);
    event.stopPropagation();
  };

  const validateStripeElement = () => {
    // get reference to the card object
    const cardNumberElement = elements?.getElement(CardNumberElement);
    const cardExpiryElement = elements?.getElement(CardExpiryElement);
    const cardCvcElement = elements?.getElement(CardCvcElement);

    const newCardError = {
      ...cardErrorMessage,
    };
    // validate card holder name
    if (cardDetails?.name === "") {
      newCardError.name = "Please input card holder name.";
    }
    // validate card number
    // eslint-disable-next-line no-underscore-dangle
    if (cardNumberElement?._empty) {
      newCardError.cardNumber = "Please input your card details.";
    } else if (
      // eslint-disable-next-line no-underscore-dangle
      cardNumberElement?._complete === false ||
      // eslint-disable-next-line no-underscore-dangle
      cardNumberElement?._invalid
    ) {
      newCardError.cardNumber = "Please enter a valid card number";
    }
    // validate card expiry
    // eslint-disable-next-line no-underscore-dangle
    if (cardExpiryElement?._empty) {
      newCardError.cardExpiry = "Please input your card expiry details.";
    } else if (
      // eslint-disable-next-line no-underscore-dangle
      cardExpiryElement?._complete === false ||
      // eslint-disable-next-line no-underscore-dangle
      cardExpiryElement?._invalid
    ) {
      newCardError.cardExpiry = "Card expiry is invalid.";
    }

    // validate card cvc
    // eslint-disable-next-line no-underscore-dangle
    if (cardCvcElement?._empty) {
      newCardError.cardCvc = "Please input your card's CVV/CVC.";
    } else if (
      // eslint-disable-next-line no-underscore-dangle
      cardCvcElement?._complete === false ||
      // eslint-disable-next-line no-underscore-dangle
      cardCvcElement?._invalid
    ) {
      newCardError.cardCvc =
        "Please copy your CVV/CVC code from the back of your card and continue with your payment.";
    }
    setCardErrorMessage(newCardError);
    return newCardError;
  };

  useEffect(() => {
    const fetchBillingDetails = async () => {
      if (!token) return;
      try {
        const response = await getContactDetails(token);
        setUserDetails(response?.data);
        setPhoneNumber(response?.data?.phone_number);
        setCountry(response?.data?.country);
      } catch (e) {
        toast.error(e);
      }
    };

    if (!userDetails) {
      fetchBillingDetails();
    }
  }, [userDetails, token]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: `${userDetails?.first_name} ${userDetails?.last_name}`,
      companyName: userDetails?.company_name,
      address1: userDetails?.address1,
      address2: userDetails?.address2,
      city: userDetails?.city,
      zipCode: userDetails?.post_code,
      state: userDetails?.state,
      phoneNumber: userDetails?.phoneNumber,
      email: userDetails?.email,
      cardHoldersName: "",
      cardNumber: "",
      cardExpiry: "",
      cardCvc: "",
    },
    validateOnChange: Yup.object({
      name: Yup.string().required("Enter your full name"),
      address1: Yup.string().required("Enter address"),
      city: Yup.string().required("Enter city"),
      zipCode: Yup.string().required("Enter zip code"),
      state: Yup.string().required("Enter state"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Enter email address"),
    }),
    validationSchema: Yup.object({
      name: Yup.string().required("Enter your full name"),
      address1: Yup.string().required("Enter address"),
      city: Yup.string().required("Enter city"),
      zipCode: Yup.string().required("Enter zip code"),
      state: Yup.string().required("Enter state"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Enter email address"),
    }),
    onSubmit: async (values) => {
      if (!stripe || !elements) {
        // Stripe.js has not loaded yet. Make sure to disable
        // form submission until Stripe.js has loaded.
        return;
      }
      const cardNumberElement = elements?.getElement(CardNumberElement);
      const cardDetailsErrorList = validateStripeElement();

      if (
        isErrorListHasValue(cardDetailsErrorList) &&
        paymentMethod === "stripe"
      ) {
        toast.error("There's some validation error");
        return;
      }

      if (phoneNumber === "") {
        setPhoneNumberError("Enter phone number");
        toast.error("Phone number is required");
        return;
      }

      if (country === "" || country === null) {
        setCountryError("Select country");
        return;
      }

      setIsProcessing(true);

      const params = {
        data: {
          first_name: userDetails?.first_name || values.name,
          last_name: userDetails?.last_name || "",
          email: values?.email || userDetails?.email || user?.email,
          address1: values?.address1,
          address2: values?.address2,
          city: values?.city,
          state: values?.state,
          country,
          post_code: values?.zipCode,
          phone_number: phoneNumber || "",
          user_type: userDetails?.user_type || "other",
        },
      };

      let response = {};
      if (!userDetails) {
        response = await createContactDetails(params, token);
      } else {
        response = await updateContactDetails(params, token);
      }

      if (SUCCESS_STATUS.includes(response.status)) {
        const { client_secret: clientSecret, payment_id: paymentId } =
          await checkoutCartItems(query, token);

        // proceed to payment
        if (paymentMethod === "stripe" && clientSecret) {
          const paymentMethodRequest = await stripe.createPaymentMethod({
            type: "card",
            card: cardNumberElement,
            billing_details: {
              address: {
                city: values?.state,
                country,
                line1: values?.streetAddress,
                line2: values?.suburb,
                postal_code: values?.zipCode,
              },
              name: values?.companyName,
            },
          });
          // check for payment status
          const confirmedCardPayment = await stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: paymentMethodRequest?.paymentMethod?.id,
            }
          );

          if (confirmedCardPayment?.paymentIntent?.status === "succeeded") {
            setTimeout(() => {
              history.push({
                pathname: "/payment-success",
                state: { token, paymentId },
              });
            }, 2000);
          } else if (confirmedCardPayment?.error) {
            toast.error(confirmedCardPayment?.error?.message);
            setIsProcessing(false);
          }
        }
        if (paymentMethod === "invoice" && paymentId) {
          setTimeout(() => {
            history.push({
              pathname: "/payment-success",
              state: { token, paymentId },
            });
          }, 2000);
        }
      }
    },
  });

  const validateField = (fieldName, value) => {
    let error;
    if (fieldName === "name") {
      error = value ? "" : "Enter card holder's name";
    }
    return error;
  };

  const validateInput = (e) => {
    e.preventDefault();
    const { value, name } = e.target;
    const fieldError = validateField(name, value);

    if (name === "name") {
      const newError = {
        ...cardErrorMessage,
        [name]: fieldError || "",
      };
      setCardErrorMessage(newError);
    }
  };

  const validateCardOnchange = (e) => {
    const newError = {
      ...cardErrorMessage,
      [e?.elementType]: e?.error?.message || "",
    };
    setCardErrorMessage(newError);
  };

  const handleChangeCardName = (event) => {
    event.preventDefault();
    validateInput(event);
    const { value, name } = event.target;
    const newField = { ...cardDetails, [name]: value };
    setCardDetails(newField);
    event.stopPropagation();
  };

  const fetchProfile = useCallback(async () => {
    if (!token) return;

    try {
      const response = await getProfile(token);
      if (response) {
        setUserProfile(response);
        setPayments(response?.payments);
      }
    } catch (e) {
      toast.error("We encountered an error.");
    }
    setIsFetchingData(false);
  }, [token]);

  useEffect(() => {
    if (userProfile) return;
    fetchProfile();
  }, [fetchProfile, userProfile]);

  if (isFetchingData) return null;

  if (
    !userDetails ||
    userDetails?.country === null ||
    userDetails?.country === ""
  ) {
    return <CountryModal contactDetails={userDetails} />;
  }

  return (
    <div className="payment">
      <Navbar type={cartItems?.item_type} />
      <div className="payment-wrapper">
        <Header type={cartItems?.item_type} />
        <Order items={cartItems} price={price} />
        <div className="payment-container">
          <ContactDetails
            formik={formik}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            phoneNumberError={phoneNumberError}
          />
          <PaymentMethod
            payments={payments}
            paymentMethod={paymentMethod}
            setPaymentMethod={handleSelectPaymentMethod}
            setPayments={setPayments}
            cardErrorMessage={cardErrorMessage}
            setCardErrorMessage={setCardErrorMessage}
            validateCardOnchange={validateCardOnchange}
            handleChangeCardName={handleChangeCardName}
            cardDetails={cardDetails}
            validateInput={validateInput}
          />
          <BillingDetails
            userDetails={userDetails}
            formik={formik}
            handleSelectCountry={handleSelectCountry}
            country={country}
            countryError={countryError}
          />
        </div>
        <Terms
          setTermsAccepted={setTermsAccepted}
          termsAccepted={termsAccepted}
        />
        <PayButton
          termsAccepted={termsAccepted}
          handleSubmit={formik.handleSubmit}
          isProcessing={isProcessing}
          paymentMethod={paymentMethod}
          price={price}
          cartItems={cartItems}
        />
      </div>
    </div>
  );
};
export default PaymentContainer;
